import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Plus, Search, Pencil, Trash2, Eye, EyeOff, ExternalLink, KeyRound, Copy, Check } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function PortalsPage() {
  const { isRole, user } = useAuth();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ portal_name: '', link: '', username: '', password: '', notes: '' });
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [decryptedPasswords, setDecryptedPasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/portals');
      setPortals(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/portals/${editing.id}`, form);
      } else {
        await api.post('/portals', form);
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ portal_name: '', link: '', username: '', password: '', notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({ portal_name: p.portal_name, link: p.link || '', username: p.username || '', password: '', notes: p.notes || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo accesso?')) return;
    try { await api.delete(`/portals/${id}`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const togglePassword = async (portalId) => {
    if (visiblePasswords[portalId]) {
      setVisiblePasswords(prev => ({ ...prev, [portalId]: false }));
      return;
    }
    // Fetch decrypted password
    if (!decryptedPasswords[portalId]) {
      try {
        const res = await api.get(`/portals/${portalId}/password`);
        setDecryptedPasswords(prev => ({ ...prev, [portalId]: res.data.password }));
      } catch (err) {
        alert('Errore nel recupero password');
        return;
      }
    }
    setVisiblePasswords(prev => ({ ...prev, [portalId]: true }));
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) { console.error(err); }
  };

  const copyPassword = async (portalId) => {
    let pwd = decryptedPasswords[portalId];
    if (!pwd) {
      try {
        const res = await api.get(`/portals/${portalId}/password`);
        pwd = res.data.password;
        setDecryptedPasswords(prev => ({ ...prev, [portalId]: pwd }));
      } catch (err) { alert('Errore'); return; }
    }
    copyToClipboard(pwd, portalId + '_pwd');
  };

  const filteredPortals = portals.filter(p =>
    p.portal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.username && p.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.notes && p.notes.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  return (
    <div className="space-y-4" data-testid="portals-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Portali e Password</h1>
        {user?.role_name === 'admin' && <Badge variant="outline" className="text-xs">Admin: vedi tutti gli accessi</Badge>}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input placeholder="Cerca portale..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-portal" />
            </div>
            <Button onClick={() => { setEditing(null); setForm({ portal_name: '', link: '', username: '', password: '', notes: '' }); setShowDialog(true); }} data-testid="add-portal-btn">
              <Plus size={16} className="mr-1" /> Nuovo Accesso
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Portale</TableHead>
                <TableHead>Link</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Password</TableHead>
                <TableHead>Note</TableHead>
                {user?.role_name === 'admin' && <TableHead>Creato da</TableHead>}
                <TableHead className="w-[100px]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPortals.length === 0 ? (
                <TableRow><TableCell colSpan={user?.role_name === 'admin' ? 7 : 6} className="text-center py-8 text-slate-400">Nessun accesso salvato</TableCell></TableRow>
              ) : filteredPortals.map(p => (
                <TableRow key={p.id} className="hover:bg-slate-50">
                  <TableCell className="font-semibold">{p.portal_name}</TableCell>
                  <TableCell className="text-sm">
                    {p.link ? <a href={p.link.startsWith('http') ? p.link : `https://${p.link}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1"><ExternalLink size={12} />Apri</a> : '-'}
                  </TableCell>
                  <TableCell className="text-sm font-mono">
                    {p.username ? (
                      <div className="flex items-center gap-1">
                        <span>{p.username}</span>
                        <button onClick={() => copyToClipboard(p.username, p.id + '_user')} className="text-slate-400 hover:text-slate-600">
                          {copiedId === p.id + '_user' ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                        </button>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-mono">
                        {visiblePasswords[p.id] ? decryptedPasswords[p.id] || '' : p.password_masked || ''}
                      </span>
                      {p.password_masked && (
                        <>
                          <button onClick={() => togglePassword(p.id)} className="text-slate-400 hover:text-slate-600 p-0.5" data-testid={`toggle-pwd-${p.id}`}>
                            {visiblePasswords[p.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button onClick={() => copyPassword(p.id)} className="text-slate-400 hover:text-slate-600 p-0.5">
                            {copiedId === p.id + '_pwd' ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                          </button>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">{p.notes || '-'}</TableCell>
                  {user?.role_name === 'admin' && <TableCell className="text-xs text-slate-400">{p.created_by_email || '-'}</TableCell>}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(p)} className="p-1 h-auto"><Pencil size={14} className="text-blue-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="p-1 h-auto"><Trash2 size={14} className="text-red-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Accesso' : 'Nuovo Accesso'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome Portale *</Label><Input value={form.portal_name} onChange={(e) => setForm({...form, portal_name: e.target.value})} placeholder="es. Portale INPS" data-testid="portal-name-input" /></div>
            <div><Label>Link</Label><Input value={form.link} onChange={(e) => setForm({...form, link: e.target.value})} placeholder="https://..." /></div>
            <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} /></div>
            <div><Label>Password {editing ? '(lascia vuoto per non modificare)' : ''}</Label><Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} /></div>
            <div><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.portal_name.trim()}>{editing ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
